import { getWindowRoleSync } from "@/desktop/use-window-role";
import { loadFrame } from "@/lib/frame-ingest";
import { useWorkspaceLoadStore } from "@/stores/workspace-load-store";
import { useFramesStore } from "@/stores/frames-store";
import { useProjectStore } from "@/stores/project-store";
import { isWallActive } from "@/stores/workspace-view-store";

export type WaitForFrameResult = "found" | "timeout";

/** Resolve once `ready()` holds, re-checked on every `subscribe` notice. */
function waitFor(
  subscribe: (listener: () => void) => () => void,
  ready: () => boolean,
  timeoutMs: number,
): Promise<WaitForFrameResult> {
  return new Promise((resolve) => {
    if (ready()) {
      resolve("found");
      return;
    }

    let done = false;
    const finish = (result: WaitForFrameResult) => {
      if (done) return;
      done = true;
      unsub();
      clearTimeout(to);
      resolve(result);
    };

    const unsub = subscribe(() => {
      if (ready()) finish("found");
    });

    if (ready()) {
      finish("found");
      return;
    }

    const to = setTimeout(() => finish("timeout"), timeoutMs);
  });
}

/**
 * The wall loads its cards a page at a time, so once the project's own load
 * has landed, a frame it did not bring is fetched by id.
 */
async function waitForWallFrame(
  frameId: string,
  timeoutMs: number,
): Promise<WaitForFrameResult> {
  const projectId = useProjectStore.getState().currentProject?.id;
  if (!projectId) return "timeout";
  const loaded = await waitFor(
    useWorkspaceLoadStore.subscribe,
    () => {
      const load = useWorkspaceLoadStore.getState();
      return (
        getWindowRoleSync() === "mirror" ||
        (!load.isLoadingCanvas && load.framesLoadedSpaceId === projectId)
      );
    },
    timeoutMs,
  );
  if (loaded === "timeout") return "timeout";
  try {
    return (await loadFrame(projectId, frameId)) ? "found" : "timeout";
  } catch {
    return "timeout";
  }
}

export function waitForFrame(
  frameId: string,
  timeoutMs = 3000,
): Promise<WaitForFrameResult> {
  if (isWallActive()) return waitForWallFrame(frameId, timeoutMs);
  return waitFor(
    useFramesStore.subscribe,
    () => !!useFramesStore.getState().framesById[frameId],
    timeoutMs,
  );
}
